import React, { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import Window from "./Window";
import Icon from "./Icon";
import {
  FINISHED_AT,
  KINDS,
  MAX_SHELF,
  PLANT_H,
  PLANT_W,
  PlantState,
  SHELF_ROWS,
  ShelfPlant,
  nextKind,
  normalizePlant,
  normalizeShelf,
  renderPlant,
  stageFor,
  stageName,
  todayKey,
  untilNext,
} from "../plant";

// Crisp pixel drawing of a plant (one rect per run of color).
export function PlantArt({
  waterings,
  kind,
  width,
  crop = false,
  onShelf = false,
}: {
  waterings: number;
  kind: string;
  width: number;
  /** trim empty sky above small plants */
  crop?: boolean;
  /** stand the pot on a little wooden shelf */
  onShelf?: boolean;
}) {
  const fullH = PLANT_H + (onShelf ? SHELF_ROWS : 0);
  const rects = useMemo(() => {
    const out: { x: number; y: number; w: number; fill: string }[] = [];
    renderPlant(waterings, kind, onShelf).forEach((row, y) => {
      let x = 0;
      while (x < PLANT_W) {
        const fill = row[x];
        if (!fill) {
          x++;
          continue;
        }
        let w = 1;
        while (x + w < PLANT_W && row[x + w] === fill) w++;
        out.push({ x, y, w, fill });
        x += w;
      }
    });
    return out;
  }, [waterings, kind, onShelf]);

  const top = crop ? Math.max(0, Math.min(...rects.map((r) => r.y), PLANT_H) - 3) : 0;
  const h = fullH - top;

  return (
    <svg
      viewBox={`0 ${top} ${PLANT_W} ${h}`}
      width={width}
      height={(width * h) / PLANT_W}
      shapeRendering="crispEdges"
      className="plant-art"
      role="img"
      aria-label={`${KINDS[kind]?.name || "Plant"}: ${stageName(kind, waterings)}`}
    >
      {rects.map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={1} fill={r.fill} />
      ))}
    </svg>
  );
}

// Live view of a person's garden (current plant + shelf) from their profile doc.
function useGarden(uid: string, live: boolean) {
  const [garden, setGarden] = useState<{ plant: PlantState; shelf: ShelfPlant[] } | null>(null);
  useEffect(() => {
    const read = (data: any) =>
      setGarden({ plant: normalizePlant(data?.plant), shelf: normalizeShelf(data?.shelf) });
    if (live) {
      return onSnapshot(doc(db, "users", uid), (snap) => read(snap.data()), () => read(null));
    }
    getDoc(doc(db, "users", uid))
      .then((snap) => read(snap.data()))
      .catch(() => setGarden(null));
  }, [uid, live]);
  return garden;
}

// Your own plant, with a once-a-day Water button.
export function MyPlant({ uid, compact = false }: { uid: string; compact?: boolean }) {
  const garden = useGarden(uid, true);
  const [watering, setWatering] = useState(false);
  const [grew, setGrew] = useState(false);
  const [busy, setBusy] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  if (!garden) return null;
  const { plant, shelf } = garden;
  const name = KINDS[plant.kind].name;
  const wateredToday = plant.lastWatered === todayKey();
  const finished = plant.waterings >= FINISHED_AT;
  const next = untilNext(plant.waterings);

  const water = async () => {
    if (wateredToday || watering) return;
    setWatering(true);
    setGrew(false);
    const grows = stageFor(plant.waterings + 1) > stageFor(plant.waterings);
    try {
      await updateDoc(doc(db, "users", uid), {
        plant: { ...plant, waterings: plant.waterings + 1, lastWatered: todayKey() },
      });
      if (grows) timers.current.push(window.setTimeout(() => setGrew(true), 900));
    } catch (err) {
      console.error("Couldn't water the plant:", err);
    }
    timers.current.push(window.setTimeout(() => setWatering(false), 1400));
  };

  const moveToShelf = async () => {
    if (!finished || busy) return;
    const upNext = KINDS[nextKind(plant.kind)].name;
    const ok = window.confirm(
      `Move your ${name} to your shelf? It'll be shown on your profile, ` +
        `and a new ${upNext} seed will take its place.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "users", uid), {
        shelf: [
          ...shelf,
          { kind: plant.kind, waterings: plant.waterings, finishedOn: todayKey() },
        ].slice(-MAX_SHELF),
        plant: { kind: nextKind(plant.kind), waterings: 0, lastWatered: plant.lastWatered },
      });
      setGrew(false);
    } catch (err) {
      console.error("Couldn't move the plant:", err);
      alert("Sorry, that didn't work. Please try again.");
    }
    setBusy(false);
  };

  const drops = watering && (
    <span className="plant-drops" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );

  // Sidebar version: small plant beside its name and the Water button.
  if (compact) {
    return (
      <Window title="my_plant.exe" tone="mint" bodyClassName="!p-2">
        <div className="flex items-center gap-2">
          <div className={`plant-stage${watering ? " is-watering" : ""}`}>
            {drops}
            <PlantArt waterings={plant.waterings} kind={plant.kind} width={42} onShelf />
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
            <p
              className="font-display text-sm font-semibold leading-tight"
              title={stageName(plant.kind, plant.waterings)}
            >
              {grew ? "✧ it grew! ✧" : name}
            </p>
            {finished ? (
              <button type="button" className="btn btn-sm !px-2" onClick={moveToShelf} disabled={busy}>
                To my shelf ✿
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-blue btn-sm !px-2"
                onClick={water}
                disabled={wateredToday || watering}
                title={
                  wateredToday && next !== null
                    ? `See you tomorrow! Something new in ${next} more watering${next === 1 ? "" : "s"}.`
                    : undefined
                }
              >
                <Icon name="drop" size={12} />
                {wateredToday ? "Watered ✿" : "Water"}
              </button>
            )}
          </div>
        </div>
      </Window>
    );
  }

  return (
    <Window title={`${plant.kind.toLowerCase()}.exe`} tone="mint" bodyClassName="!p-3">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className={`plant-stage${watering ? " is-watering" : ""}`}>
          {drops}
          <PlantArt waterings={plant.waterings} kind={plant.kind} width={120} crop onShelf />
        </div>

        <p className="font-display text-sm font-semibold">{name}</p>
        {grew ? (
          <p className="pickup-note" role="status">
            ✧ it grew! ✧
          </p>
        ) : (
          <p className="text-xs text-ink-soft">
            {plant.waterings === 0
              ? "a seed, waiting for its first drink"
              : `${stageName(plant.kind, plant.waterings)} · watered ${plant.waterings}×`}
          </p>
        )}

        <button
          type="button"
          className="btn btn-blue btn-sm"
          onClick={water}
          disabled={wateredToday || watering}
        >
          <Icon name="drop" size={14} />
          {wateredToday ? "Watered today ✿" : "Water"}
        </button>

        {finished && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={moveToShelf}
            disabled={busy}
          >
            <Icon name="stars" size={14} /> Move to my shelf ✿
          </button>
        )}

        {wateredToday && !finished && next !== null && (
          <p className="text-[0.7rem] text-ink-soft">
            see you tomorrow! something new in {next} more watering
            {next === 1 ? "" : "s"}
          </p>
        )}
      </div>
    </Window>
  );
}

// A wooden shelf of finished plants.
export function PlantShelf({ shelf, title }: { shelf: ShelfPlant[]; title: string }) {
  return (
    <div className="profile-box">
      <div className="profile-box-head">{title}</div>
      <div className="profile-box-body">
        {shelf.length === 0 ? (
          <p className="text-sm text-ink-soft">
            Empty for now. Grow a plant to full size and move it here!
          </p>
        ) : (
          <ul className="plant-shelf" aria-label="Finished plants">
            {shelf.map((p, i) => (
              <li key={i} title={`${KINDS[p.kind].name}${p.finishedOn ? `, grown ${p.finishedOn}` : ""}`}>
                <PlantArt waterings={p.waterings} kind={p.kind} width={60} />
                <span className="plant-shelf-label">{KINDS[p.kind].name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Your shelf on the My Profile page (live).
export function MyShelf({ uid }: { uid: string }) {
  const garden = useGarden(uid, true);
  if (!garden) return null;
  return <PlantShelf shelf={garden.shelf} title={`my plant shelf (${garden.shelf.length})`} />;
}

// Someone else's garden, for their profile: size only, never when they visited.
export function TheirGarden({ uid, username }: { uid: string; username: string }) {
  const garden = useGarden(uid, false);
  if (!garden) return null;
  const { plant, shelf } = garden;
  if (plant.waterings === 0 && shelf.length === 0) return null;
  return (
    <>
      {plant.waterings > 0 && (
        <div className="profile-box">
          <div className="profile-box-head">@{username}'s plant</div>
          <div className="profile-box-body flex flex-col items-center gap-1 text-center">
            <PlantArt waterings={plant.waterings} kind={plant.kind} width={110} crop onShelf />
            <p className="text-xs text-ink-soft">
              {KINDS[plant.kind].name} · {stageName(plant.kind, plant.waterings)}
            </p>
          </div>
        </div>
      )}
      {shelf.length > 0 && (
        <PlantShelf shelf={shelf} title={`@${username}'s plant shelf (${shelf.length})`} />
      )}
    </>
  );
}
